import gql from "graphql-tag";

import { Background } from "components/Background/Backgound";
import { NextPageButton } from "components/Button/NextPageButton";
import { PaginatedQuery } from "components/Query/PaginatedQuery";
import { Table } from "components/Table/Table";
import {
  FeedbackReportFragment,
  GetFeedbackReports,
} from "generated/copilot-api-developer";
import { useTranslation } from "i18n";

import { StatusBadge } from "./StatusBadge";

gql`
  # schema = COPILOT_API_DEVELOPER
  query GetFeedbackReports($cursor: String) {
    feedbackReports(page: { numberOfItems: 20, cursor: $cursor }) {
      nextCursor
      hasMore
      data {
        ...FeedbackReport
      }
    }
  }

  fragment FeedbackReport on FeedbackReport {
    uuid
    feedbackId
    comment
    status
    user {
      externalId
    }
    createdAt
    rating
  }
`;

const formatDateTime = (date: FeedbackReportFragment["createdAt"]) =>
  `${date.format("dateAndTime")}`;

const Rating = ({ rating }: { rating: number | null }) => {
  if (rating === null) return null;
  return (
    <div className="text-warning whitespace-nowrap" title={`${rating}/5`}>
      {"★".repeat(rating)}
      <span className="text-grey-200">{"★".repeat(5 - rating)}</span>
    </div>
  );
};

export const FeedbackReports = () => {
  const t = useTranslation();

  return (
    <Background className="flex-col flex-fill overflow-auto p-16 lg:p-44 space-y-24">
      <div className="flex-col">
        <h1 className="text-primary-dark text-24 font-bold">
          {t("developers.feedback_reports.feedback_reports")}
        </h1>
        <p>{t("developers.feedback_reports.description")}</p>
      </div>
      <PaginatedQuery query={GetFeedbackReports} pollInterval={60_000}>
        {({ data }, utils) => (
          <>
            <Table
              elements={data}
              fieldHeaders={[
                t("developers.feedback_reports.feedback_id"),
                t("developers.feedback_reports.created_at"),
                t("developers.feedback_reports.external_user_id"),
                t("developers.feedback_reports.comment"),
                t("developers.feedback_reports.rating"),
                t("developers.feedback_reports.status"),
              ]}
              fields={(report) => [
                <div
                  className="truncate max-w-[200px]"
                  title={report.feedbackId ?? undefined}
                  key="id"
                >
                  {report.feedbackId}
                </div>,
                <div className="whitespace-nowrap" key="date">
                  {formatDateTime(report.createdAt)}
                </div>,
                <div
                  className="max-w-[160px] line-clamp-2 break-words"
                  title={report.user?.externalId ?? undefined}
                  key="user"
                >
                  {report.user?.externalId}
                </div>,
                <div
                  className="min-w-[300px] max-w-[520px] line-clamp-2 whitespace-pre-wrap break-words"
                  title={report.comment ?? undefined}
                  key="comment"
                >
                  {report.comment}
                </div>,
                <Rating rating={report.rating} key="rating" />,
                <StatusBadge status={report.status} key="st" />,
              ]}
            />
            <NextPageButton {...utils} />
          </>
        )}
      </PaginatedQuery>
    </Background>
  );
};
